/* ===================================================================
   CORPUS IMPORT — parse a dropped/picked file into the app's email model.

   Zero external dependencies. Supported formats:
     .mbox   standard mbox export (Gmail/Thunderbird) — many messages
     .eml    one RFC822 message, OR a .zip of many .eml files
     .zip    archive of .eml files, OR a corpus folder: one .json/.mbox/.csv
             email source + real attachment files (PDF/DOCX/XLSX…) matched
             to declared attachments by filename
     .json   array of email objects (flexible field names)
     .csv    columns: from, to, cc, subject, date, body

   Every parser emits the same shape the editor uses:
     { from, fromName, to:[], cc:[], subject, time, raw, attachments:[] }
   (ids are assigned by the caller when the corpus is built.)

   parseEmailFile resolves to { format, emails, warnings }.
=================================================================== */

/* ---- small utilities ---------------------------------------------------- */

const td = new TextDecoder("utf-8", { fatal: false });

function decodeBytesUtf8(bytes) {
  try { return td.decode(bytes); } catch { return ""; }
}

/* base64 → Uint8Array (tolerant of whitespace) */
function b64ToBytes(b64) {
  const clean = String(b64 || "").replace(/[^A-Za-z0-9+/=]/g, "");
  let bin;
  try { bin = atob(clean); } catch { return new Uint8Array(0); }
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToDataUrl(bytes, mime) {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  let b64 = "";
  try { b64 = btoa(bin); } catch { b64 = ""; }
  return "data:" + (mime || "application/octet-stream") + ";base64," + b64;
}

/* ---- attachment file pool ----------------------------------------------
   Real corpora arrive as an email source file plus actual documents (PDF,
   DOCX, XLSX…). The pool matches those binaries by filename (basename,
   case-insensitive) against the attachments the emails declare. */

const MIME_BY_EXT: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  rtf: "application/rtf",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
};

export function mimeForFilename(name: string): string {
  return MIME_BY_EXT[extOf(name)] || "application/octet-stream";
}

export const ATTACHMENT_FILE_EXTS = [
  "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "rtf", "txt", "md",
  "png", "jpg", "jpeg", "gif",
];

export function isAttachmentFileName(name: string): boolean {
  return ATTACHMENT_FILE_EXTS.includes(extOf(name));
}

function baseName(path: string): string {
  const s = String(path || "");
  const i = s.lastIndexOf("/");
  return i === -1 ? s : s.slice(i + 1);
}

export interface PoolFile {
  name: string;
  dataUrl: string;
}

export interface PoolMatchResult {
  matched: number;
  unmatchedFiles: string[];
  missingAttachments: string[];
}

/* Fill emails' attachment fileData from the pool, matching by basename
   (case-insensitive). Mutates the attachment objects in place; callers that
   need immutability clone first. The same file may attach to several emails. */
export function attachFilesToEmails(emails: any[], files: PoolFile[]): PoolMatchResult {
  const pool = new Map<string, PoolFile>();
  for (const f of files) pool.set(baseName(f.name).toLowerCase(), f);
  const used = new Set<string>();
  const missing = new Set<string>();
  for (const e of emails || []) {
    for (const a of e.attachments || []) {
      const key = String(a.filename || "").toLowerCase();
      const f = pool.get(key);
      if (f) {
        a.fileData = f.dataUrl;
        used.add(key);
      } else if (!a.fileData) {
        missing.add(String(a.filename || ""));
      }
    }
  }
  const unmatchedFiles = [...pool.keys()]
    .filter((k) => !used.has(k))
    .map((k) => baseName((pool.get(k) as PoolFile).name));
  return { matched: used.size, unmatchedFiles, missingAttachments: [...missing] };
}

/* RFC 2047 encoded-word decode: =?charset?B|Q?text?= (used in headers) */
function decodeEncodedWords(str) {
  if (!str || str.indexOf("=?") === -1) return str || "";
  return str.replace(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g, (_, _cs, enc, text) => {
    try {
      if (enc.toUpperCase() === "B") return decodeBytesUtf8(b64ToBytes(text));
      // Q-encoding: _ is space, =XX is a hex byte
      const bytes: number[] = [];
      for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (c === "_") bytes.push(0x20);
        else if (c === "=" && /[0-9A-Fa-f]{2}/.test(text.substr(i + 1, 2))) {
          bytes.push(parseInt(text.substr(i + 1, 2), 16)); i += 2;
        } else bytes.push(c.charCodeAt(0));
      }
      return decodeBytesUtf8(new Uint8Array(bytes));
    } catch { return text; }
  }).replace(/\?=\s+=\?/g, "?==?"); // join adjacent words separated by whitespace
}

/* quoted-printable decode (text bodies / parts) */
function decodeQuotedPrintable(str) {
  const s = String(str || "").replace(/=\r?\n/g, ""); // soft line breaks
  const bytes: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === "=" && /[0-9A-Fa-f]{2}/.test(s.substr(i + 1, 2))) {
      bytes.push(parseInt(s.substr(i + 1, 2), 16)); i += 2;
    } else bytes.push(c.charCodeAt(0) & 0xff);
  }
  return decodeBytesUtf8(new Uint8Array(bytes));
}

function stripHtml(html) {
  return String(html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<\/(p|div|tr|h[1-6]|li)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/* format a Date into the app's display style: "Apr 06, 2026 · 5:42 PM" */
function fmtTime(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return date + " · " + time;
}

/* parse a date from a header/cell; falls back to the original string */
function parseDate(raw) {
  const s = String(raw || "").trim();
  if (!s) return { time: "", date: null };
  const d = new Date(s);
  if (!isNaN(d.getTime())) return { time: fmtTime(d), date: d };
  return { time: s, date: null }; // keep whatever the source had
}

/* ---- address parsing ---------------------------------------------------- */

function parseOneAddr(s) {
  const t = String(s || "").trim();
  const m = t.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/);
  if (m) return { name: decodeEncodedWords(m[1]).trim(), email: m[2].trim() };
  return { name: "", email: t.replace(/[<>]/g, "").trim() };
}

/* split a header value on commas that are outside quotes/angle brackets */
function splitAddrList(s) {
  const out: string[] = [];
  let cur = "", inQ = false, inA = false;
  for (const ch of String(s || "")) {
    if (ch === '"') inQ = !inQ;
    else if (ch === "<") inA = true;
    else if (ch === ">") inA = false;
    if (ch === "," && !inQ && !inA) { out.push(cur); cur = ""; }
    else cur += ch;
  }
  if (cur.trim()) out.push(cur);
  return out.map((x) => x.trim()).filter(Boolean);
}

function addrEmails(headerVal) {
  return splitAddrList(decodeEncodedWords(headerVal)).map((a) => parseOneAddr(a).email).filter(Boolean);
}

/* ---- RFC822 / MIME message parser -------------------------------------- */

/* Split a raw message into { headers (lowercased keys), body } */
function splitHeaders(raw) {
  const norm = String(raw || "").replace(/\r\n/g, "\n");
  const idx = norm.indexOf("\n\n");
  const headBlock = idx === -1 ? norm : norm.slice(0, idx);
  const body = idx === -1 ? "" : norm.slice(idx + 2);
  // unfold: a line starting with space/tab continues the previous header
  const unfolded = headBlock.replace(/\n[ \t]+/g, " ");
  const headers: Record<string, string> = {};
  const order: string[] = [];
  unfolded.split("\n").forEach((line) => {
    const c = line.indexOf(":");
    if (c === -1) return;
    const key = line.slice(0, c).trim().toLowerCase();
    const val = line.slice(c + 1).trim();
    if (key in headers) headers[key] += ", " + val; else { headers[key] = val; order.push(key); }
  });
  return { headers, body };
}

function getParam(headerVal, name) {
  const re = new RegExp(name + '\\s*=\\s*"([^"]*)"|' + name + "\\s*=\\s*([^;\\s]+)", "i");
  const m = re.exec(headerVal || "");
  return m ? (m[1] != null ? m[1] : m[2]) : "";
}

function decodeBody(body, encoding) {
  const enc = String(encoding || "").toLowerCase();
  if (enc === "base64") return decodeBytesUtf8(b64ToBytes(body));
  if (enc === "quoted-printable") return decodeQuotedPrintable(body);
  return body;
}

/* Walk a (possibly multipart) message; collect best text body + attachments */
function walkPart(raw, opts, acc) {
  const { headers, body } = splitHeaders(raw);
  const ctype = (headers["content-type"] || "text/plain").toLowerCase();
  const cdisp = (headers["content-disposition"] || "").toLowerCase();
  const enc = headers["content-transfer-encoding"] || "";

  if (ctype.startsWith("multipart/")) {
    const boundary = getParam(headers["content-type"], "boundary");
    if (!boundary) return;
    const marker = "--" + boundary;
    const segments = body.split(marker);
    // first segment is the preamble, last is the closing "--"
    for (let i = 1; i < segments.length; i++) {
      let seg = segments[i];
      if (seg.startsWith("--")) break; // closing boundary
      if (seg.startsWith("\n")) seg = seg.slice(1);
      walkPart(seg.replace(/\n$/, ""), opts, acc);
    }
    return;
  }

  const filename = decodeEncodedWords(getParam(headers["content-disposition"], "filename") || getParam(headers["content-type"], "name"));
  const isAttachment = cdisp.startsWith("attachment") || (!!filename && !ctype.startsWith("text/"));

  if (isAttachment && filename) {
    const bytes = String(enc).toLowerCase() === "base64"
      ? b64ToBytes(body)
      : new Uint8Array([...decodeBody(body, enc)].map((c) => c.charCodeAt(0) & 0xff));
    const isText = ctype.startsWith("text/");
    acc.attachments.push({
      filename,
      doc_type: opts.guessDocType(filename),
      version: 1,
      content: isText ? decodeBody(body, enc) : "",
      fileData: bytesToDataUrl(bytes, (ctype.split(";")[0] || "application/octet-stream").trim()),
    });
    return;
  }

  const text = decodeBody(body, enc);
  if (ctype.startsWith("text/plain") && !acc.text) acc.text = text;
  else if (ctype.startsWith("text/html") && !acc.html) acc.html = text;
  else if (!acc.text && !ctype.startsWith("text/html")) acc.text = text;
}

function parseRfc822(raw, opts) {
  const { headers } = splitHeaders(raw);
  const acc = { text: "", html: "", attachments: [] };
  walkPart(raw, opts, acc);
  const from = parseOneAddr(decodeEncodedWords(headers["from"] || ""));
  const body = acc.text || (acc.html ? stripHtml(acc.html) : "");
  return {
    from: from.email,
    fromName: from.name,
    to: addrEmails(headers["to"] || ""),
    cc: addrEmails(headers["cc"] || ""),
    subject: decodeEncodedWords(headers["subject"] || ""),
    time: parseDate(headers["date"] || "").time,
    raw: body.replace(/\r\n/g, "\n").replace(/[ \t]+\n/g, "\n").trim(),
    attachments: acc.attachments,
  };
}

/* ---- mbox -------------------------------------------------------------- */

function parseMbox(text, opts) {
  const norm = text.replace(/\r\n/g, "\n");
  // messages begin at a line starting with "From " (the mbox separator)
  const parts = norm.split(/\n(?=From )/g);
  const emails: any[] = [];
  for (let chunk of parts) {
    // drop the leading "From ..." envelope line if present
    chunk = chunk.replace(/^From .*\n/, "");
    if (!chunk.trim()) continue;
    // unmangle mboxrd ">From " → "From "
    chunk = chunk.replace(/\n>(>*From )/g, "\n$1");
    emails.push(parseRfc822(chunk, opts));
  }
  return emails;
}

/* ---- zip (DecompressionStream, deflate-raw) ---------------------------- */

async function inflateRaw(bytes) {
  if (typeof DecompressionStream === "undefined") throw new Error("DecompressionStream unavailable in this browser");
  const ds = new DecompressionStream("deflate-raw");
  const stream = new Blob([bytes]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function unzip(arrayBuffer) {
  const dv = new DataView(arrayBuffer);
  const u8 = new Uint8Array(arrayBuffer);
  const n = dv.byteLength;
  // locate End Of Central Directory record (sig 0x06054b50), scanning from end
  let eocd = -1;
  for (let i = n - 22; i >= 0 && i >= n - 22 - 65536; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd === -1) throw new Error("not a valid zip file");
  const cdCount = dv.getUint16(eocd + 10, true);
  let cd = dv.getUint32(eocd + 16, true);
  const files: Array<{ name: string; method: number; comp: Uint8Array }> = [];
  for (let e = 0; e < cdCount; e++) {
    if (dv.getUint32(cd, true) !== 0x02014b50) break;
    const method = dv.getUint16(cd + 10, true);
    const compSize = dv.getUint32(cd + 20, true);
    const nameLen = dv.getUint16(cd + 28, true);
    const extraLen = dv.getUint16(cd + 30, true);
    const commentLen = dv.getUint16(cd + 32, true);
    const localOff = dv.getUint32(cd + 42, true);
    const name = decodeBytesUtf8(u8.subarray(cd + 46, cd + 46 + nameLen));
    // local header → data offset
    const lhNameLen = dv.getUint16(localOff + 26, true);
    const lhExtraLen = dv.getUint16(localOff + 28, true);
    const dataStart = localOff + 30 + lhNameLen + lhExtraLen;
    const comp = u8.subarray(dataStart, dataStart + compSize);
    files.push({ name, method, comp });
    cd += 46 + nameLen + extraLen + commentLen;
  }
  const out: Array<{ name: string; bytes: Uint8Array }> = [];
  for (const f of files) {
    if (/\/$/.test(f.name)) continue; // directory entry
    let bytes: Uint8Array;
    if (f.method === 0) bytes = f.comp;
    else if (f.method === 8) bytes = await inflateRaw(f.comp);
    else continue; // unsupported compression
    out.push({ name: f.name, bytes });
  }
  return out;
}

/* Parse a zip archive. Two shapes are supported:
     · an archive of .eml files (the original behavior), and
     · a "corpus folder" zip: one email source (.mbox / .json / .csv) plus
       real document files anywhere in the archive (e.g. attachments/),
       matched to the emails' declared attachments by filename. */
async function parseZip(arrayBuffer, opts) {
  const entries = (await unzip(arrayBuffer)).filter(
    (e) => !/^__MACOSX\//.test(e.name) && !/(^|\/)\./.test(e.name),
  );
  const warnings: string[] = [];
  const emls = entries.filter((e) => /\.eml$/i.test(e.name));
  let emails: any[] = [];
  let source: { name: string; bytes: Uint8Array } | null = null;
  if (emls.length) {
    for (const e of emls) emails.push(parseRfc822(decodeBytesUtf8(e.bytes), opts));
  } else {
    source =
      entries.find((e) => /\.mbox$/i.test(e.name)) ||
      entries.find((e) => /\.json$/i.test(e.name)) ||
      entries.find((e) => /\.csv$/i.test(e.name)) ||
      null;
    if (source) {
      const text = decodeBytesUtf8(source.bytes);
      try {
        if (/\.mbox$/i.test(source.name)) emails = parseMbox(text, opts);
        else if (/\.json$/i.test(source.name)) emails = parseJson(text, opts);
        else emails = parseCsv(text, opts);
      } catch (err) {
        warnings.push("Could not parse " + baseName(source.name) + ": " + String((err as any)?.message || err));
      }
    }
  }
  const pool: PoolFile[] = entries
    .filter((e) => e !== source && !/\.eml$/i.test(e.name) && isAttachmentFileName(e.name))
    .map((e) => ({ name: e.name, dataUrl: bytesToDataUrl(e.bytes, mimeForFilename(e.name)) }));
  if (emails.length && pool.length) {
    const m = attachFilesToEmails(emails, pool);
    if (m.unmatchedFiles.length)
      warnings.push(
        m.unmatchedFiles.length + " file" + (m.unmatchedFiles.length === 1 ? "" : "s") +
          " in the archive matched no email attachment: " + m.unmatchedFiles.slice(0, 5).join(", ") +
          (m.unmatchedFiles.length > 5 ? "…" : ""),
      );
    if (m.missingAttachments.length)
      warnings.push(
        m.missingAttachments.length + " referenced attachment" + (m.missingAttachments.length === 1 ? "" : "s") +
          " had no file in the archive: " + m.missingAttachments.slice(0, 5).join(", ") +
          (m.missingAttachments.length > 5 ? "…" : ""),
      );
  }
  return { emails, warnings };
}

/* ---- json -------------------------------------------------------------- */

function pick(obj, keys) {
  for (const k of keys) {
    const found = Object.keys(obj).find((kk) => kk.toLowerCase() === k);
    if (found != null && obj[found] != null && obj[found] !== "") return obj[found];
  }
  return undefined;
}

function asList(v) {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (v == null) return [];
  return String(v).split(/[;,]/).map((x) => x.trim()).filter(Boolean);
}

function normalizeJsonEmail(o, opts) {
  if (typeof o !== "object" || !o) return null;
  const fromRaw = pick(o, ["from", "sender", "fromemail"]);
  const parsedFrom = parseOneAddr(String(fromRaw || ""));
  const fromName = pick(o, ["fromname", "name", "sendername"]) || parsedFrom.name;
  const from = parsedFrom.email || String(fromRaw || "");
  const date = pick(o, ["time", "date", "sent", "datetime"]);
  const atts = pick(o, ["attachments", "files"]);
  let attachments: any[] = [];
  if (Array.isArray(atts)) {
    attachments = atts.map((a) => {
      if (typeof a === "string") return { filename: a, doc_type: opts.guessDocType(a), version: 1, content: "" };
      const fn = pick(a, ["filename", "name", "file"]) || "attachment";
      return {
        filename: String(fn),
        doc_type: pick(a, ["doc_type", "type"]) || opts.guessDocType(fn),
        version: Number(pick(a, ["version"])) || 1,
        content: String(pick(a, ["content", "text", "body"]) || ""),
        ...(pick(a, ["filedata", "data"]) ? { fileData: pick(a, ["filedata", "data"]) } : {}),
      };
    });
  }
  return {
    from,
    fromName: String(fromName || ""),
    to: asList(pick(o, ["to", "recipients", "toemail"])),
    cc: asList(pick(o, ["cc"])),
    subject: String(pick(o, ["subject", "title"]) || ""),
    time: parseDate(date).time || String(date || ""),
    raw: String(pick(o, ["raw", "body", "text", "content", "message"]) || ""),
    attachments,
  };
}

function parseJson(text, opts) {
  const data = JSON.parse(text);
  const arr = Array.isArray(data) ? data : (Array.isArray(data.emails) ? data.emails : [data]);
  return arr.map((o) => normalizeJsonEmail(o, opts)).filter(Boolean);
}

/* ---- csv --------------------------------------------------------------- */

/* RFC4180-ish parser: handles quoted fields, embedded commas/newlines, "" */
export function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], field = "", inQ = false;
  const s = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQ) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; }
        else inQ = false;
      } else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ""));
}

function parseCsv(text, opts) {
  const rows = parseCsvRows(text);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const col = (names) => header.findIndex((h) => names.includes(h));
  const ci = {
    from: col(["from", "sender"]),
    fromName: col(["fromname", "from name", "name"]),
    to: col(["to", "recipients"]),
    cc: col(["cc"]),
    subject: col(["subject", "title"]),
    date: col(["date", "time", "sent"]),
    body: col(["body", "raw", "text", "message"]),
  };
  const get = (r, i) => (i >= 0 && i < r.length ? String(r[i]).trim() : "");
  return rows.slice(1).map((r) => {
    const parsedFrom = parseOneAddr(get(r, ci.from));
    const date = get(r, ci.date);
    return {
      from: parsedFrom.email || get(r, ci.from),
      fromName: get(r, ci.fromName) || parsedFrom.name,
      to: asList(get(r, ci.to)),
      cc: asList(get(r, ci.cc)),
      subject: get(r, ci.subject),
      time: parseDate(date).time || date,
      raw: get(r, ci.body),
      attachments: [],
    };
  });
}

/* ---- public API -------------------------------------------------------- */

function readText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(r.error);
    r.readAsText(file);
  });
}
function readArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(file);
  });
}

function extOf(name) {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(name || ""));
  return m ? m[1].toLowerCase() : "";
}

export const SUPPORTED_EXT = ["mbox", "eml", "zip", "json", "csv"];

/* Parse a File into { format, emails, warnings }. Throws on unsupported/unreadable. */
export async function parseEmailFile(file: File, options: { guessDocType?: (name: string) => string } = {}) {
  const opts = { guessDocType: options.guessDocType || (() => "other") };
  const ext = extOf(file.name);
  const warnings: string[] = [];
  let emails: any[] = [];
  let format = ext;

  if (ext === "mbox") {
    emails = parseMbox(await readText(file), opts);
  } else if (ext === "eml") {
    emails = [parseRfc822(await readText(file), opts)];
  } else if (ext === "zip") {
    const r = await parseZip(await readArrayBuffer(file), opts);
    emails = r.emails;
    warnings.push(...r.warnings);
    if (emails.length === 0 && r.warnings.length === 0)
      warnings.push("No emails found in the archive — include .eml files, or a .json / .mbox / .csv corpus file.");
  } else if (ext === "json") {
    emails = parseJson(await readText(file), opts);
  } else if (ext === "csv") {
    emails = parseCsv(await readText(file), opts);
  } else {
    throw new Error("Unsupported file type \"." + ext + "\". Use .mbox, .eml, .zip, .json or .csv.");
  }

  // drop fully-empty rows; warn on emails missing both subject and body
  emails = emails.filter((e) => e && (e.subject || e.raw || e.from || (e.to && e.to.length)));
  const blank = emails.filter((e) => !e.subject && !e.raw).length;
  if (blank) warnings.push(blank + " email" + (blank === 1 ? "" : "s") + " had no subject or body.");
  if (emails.length === 0 && warnings.length === 0) warnings.push("No emails could be parsed from this file.");

  return { format, emails, warnings };
}

/* Build a preview summary for the confirm step. */
export function summarize(emails) {
  const subjects = emails.map((e) => e.subject).filter(Boolean);
  const dates = emails.map((e) => e.time).filter(Boolean);
  const withAtt = emails.reduce((n, e) => n + ((e.attachments && e.attachments.length) ? 1 : 0), 0);
  const attCount = emails.reduce((n, e) => n + (e.attachments ? e.attachments.length : 0), 0);
  return {
    count: emails.length,
    sampleSubjects: subjects.slice(0, 5),
    firstDate: dates[0] || "",
    lastDate: dates[dates.length - 1] || "",
    withAttachments: withAtt,
    attachmentCount: attCount,
  };
}
